import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Upload, Shield, CheckCircle2, ArrowLeft, QrCode, Smartphone, Copy, Zap } from "lucide-react";

export default function Payment() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [status, setStatus] = useState(null);
  const [intent, setIntent] = useState(null);
  const [utr, setUtr] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [phonepeLoading, setPhonepeLoading] = useState(false);
  const fileRef = useRef(null);

  const payWithPhonePe = async () => {
    if (!plan) return;
    setPhonepeLoading(true);
    try {
      const r = await api.post("/phonepe/initiate", { plan_id: planId });
      window.location.href = r.data.redirect_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "PhonePe failed to start");
      setPhonepeLoading(false);
    }
  };

  useEffect(() => {
    api.get(`/plans/${planId}`).then((r) => setPlan(r.data)).catch(() => {
      toast.error("Plan not found"); navigate("/plans");
    });
    api.get("/subscription/status").then((r) => setStatus(r.data));
  }, [planId, navigate]);

  useEffect(() => {
    if (!status || !plan) return;
    if (status.state === "ACTIVE" || status.state === "PAYMENT_PENDING") {
      toast.info(status.state === "ACTIVE" ? "You already have an active subscription" : "A payment is already pending verification");
      navigate("/dashboard");
      return;
    }
    // Mint payment intent (order_ref + upi_url)
    api.post("/payment/intent", { plan_id: planId }).then((r) => setIntent(r.data)).catch(() => {});
  }, [status, plan, planId, navigate]);

  const onFile = (f) => {
    if (!f) return;
    const allowed = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowed.includes(f.type)) { toast.error("Only JPG, PNG, or WebP allowed"); return; }
    if (f.size > 8 * 1024 * 1024) { toast.error("File too large (max 8MB)"); return; }
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(f);
  };

  const payNow = () => {
    if (!intent?.upi_url) { toast.error("Payment link not ready"); return; }
    // Opens UPI app on mobile. On desktop browsers it will no-op; QR fallback is shown.
    window.location.href = intent.upi_url;
  };

  const copyOrderRef = () => {
    if (!intent?.order_ref) return;
    navigator.clipboard.writeText(intent.order_ref).then(() => toast.success("Order reference copied"));
  };

  const submit = async () => {
    if (!file) { toast.error("Please upload the payment screenshot"); return; }
    if (!utr.trim()) { toast.error("UTR / Transaction ID is required"); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("plan_id", planId);
      fd.append("utr", utr.trim());
      fd.append("screenshot", file);
      if (intent?.order_ref) fd.append("order_ref", intent.order_ref);
      await api.post("/payment/submit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Payment submitted! Admin will verify shortly.");
      navigate("/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!plan) {
    return (<div className="min-h-screen bg-slate-50"><AppHeader /><div className="max-w-2xl mx-auto py-20 text-center text-slate-500">Loading plan...</div></div>);
  }

  return (
    <div className="min-h-screen bg-slate-50 grain-bg">
      <AppHeader />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <button data-testid="payment-back-btn" onClick={() => navigate("/plans")} className="text-sm text-teal-700 font-semibold inline-flex items-center gap-1 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to plans
        </button>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Complete Your Payment</h1>
        <p className="text-slate-600 mt-2">Pay via UPI, then upload the screenshot + UTR. Your subscription activates only after admin verification.</p>

        <div className="mt-8 grid lg:grid-cols-2 gap-6">
          {/* Pay side */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Selected Plan</div>
                <div className="font-heading text-2xl font-extrabold text-slate-900">{plan.name}</div>
                <div className="text-sm text-slate-600">{plan.description}</div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Amount</div>
                <div className="font-heading text-3xl font-extrabold text-teal-700" data-testid="payment-amount">₹{plan.price}</div>
                <div className="text-xs text-slate-500">{plan.duration_days} days · {plan.billing_cycle}</div>
              </div>
            </div>

            <Button
              data-testid="pay-phonepe-btn"
              onClick={payWithPhonePe}
              disabled={!plan || phonepeLoading}
              className="mt-5 w-full h-12 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-full font-bold text-base shadow-lg"
            >
              <Zap className="w-5 h-5 mr-2" /> {phonepeLoading ? "Opening PhonePe..." : `Pay ₹${plan.price} with PhonePe · Auto-verify`}
            </Button>
            <p className="mt-1 text-[11px] text-center text-emerald-700 font-semibold">⚡ Instant activation — no screenshot needed</p>

            <div className="my-5 flex items-center gap-3 text-[11px] uppercase font-bold tracking-widest text-slate-400">
              <div className="flex-1 h-px bg-slate-200"></div> OR MANUAL UPI <div className="flex-1 h-px bg-slate-200"></div>
            </div>

            <Button
              data-testid="pay-now-upi-btn"
              onClick={payNow}
              disabled={!intent}
              className="w-full h-12 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white rounded-full font-bold text-base shadow-lg"
            >
              <Smartphone className="w-5 h-5 mr-2" /> Pay ₹{plan.price} in UPI app
            </Button>
            <p className="mt-2 text-[11px] text-center text-slate-500">Opens GPay / PhonePe / Paytm / BHIM with amount pre-filled</p>

            {intent && (
              <div className="mt-5 rounded-xl bg-slate-50 border border-slate-200 p-3 text-xs space-y-1" data-testid="payment-intent-details">
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">Merchant UPI ID</span>
                  <span className="font-mono font-semibold text-slate-800">{intent.merchant_upi}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">Order reference</span>
                  <button data-testid="copy-order-ref" onClick={copyOrderRef} className="font-mono font-semibold text-teal-700 flex items-center gap-1 hover:text-teal-800">
                    {intent.order_ref} <Copy className="w-3 h-3" />
                  </button>
                </div>
              </div>
            )}

            <div className="mt-5 border-t border-slate-200 pt-5">
              <div className="flex items-center gap-2 text-slate-700 font-semibold text-sm">
                <QrCode className="w-4 h-4 text-teal-600" /> On desktop? Scan with any UPI app
              </div>
              <div className="mt-3 flex items-center justify-center bg-slate-50 rounded-xl p-4 border border-slate-200">
                <img data-testid="payment-qr-image" src="/payment_qr.jpeg" alt="MyLiveFriend Payment QR" className="max-w-[220px] w-full h-auto rounded-lg" />
              </div>
            </div>

            <div className="mt-5 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
              <Shield className="w-4 h-4 shrink-0 mt-0.5" />
              <span>Your subscription activates only after admin verification. Never share your UPI PIN or OTP.</span>
            </div>
          </div>

          {/* Verify side */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center gap-2 text-slate-800 font-semibold">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" /> After payment, verify here
            </div>
            <ol className="mt-2 text-xs text-slate-500 list-decimal list-inside space-y-0.5">
              <li>Take a screenshot of the UPI success screen</li>
              <li>Upload it below and paste the UTR / Transaction ID</li>
              <li>Submit — admin will activate your subscription</li>
            </ol>

            <div className="mt-5 space-y-5">
              <div>
                <Label>Payment Screenshot <span className="text-rose-500">*</span></Label>
                <div
                  data-testid="payment-upload-zone"
                  onClick={() => fileRef.current?.click()}
                  className="mt-1 border-2 border-dashed border-slate-300 rounded-xl p-4 text-center cursor-pointer hover:border-teal-500 hover:bg-teal-50/40 transition"
                >
                  {preview ? (
                    <img src={preview} alt="Preview" className="max-h-48 mx-auto rounded-lg" />
                  ) : (
                    <>
                      <Upload className="w-8 h-8 mx-auto text-slate-400" />
                      <p className="mt-2 text-sm font-medium text-slate-700">Click to upload payment screenshot</p>
                      <p className="text-xs text-slate-500">JPG, PNG or WebP · max 8MB</p>
                    </>
                  )}
                  <input ref={fileRef} data-testid="payment-screenshot-input" type="file" accept="image/jpeg,image/jpg,image/png,image/webp" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
                </div>
                {file && (<p className="mt-1 text-xs text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> {file.name}</p>)}
              </div>

              <div>
                <Label htmlFor="utr">UTR / Transaction ID <span className="text-rose-500">*</span></Label>
                <Input data-testid="payment-utr-input" id="utr" value={utr} onChange={(e) => setUtr(e.target.value)} placeholder="e.g. 452398761234" className="mt-1 font-mono" />
                <p className="text-xs text-slate-500 mt-1">Find this in your UPI app under transaction details.</p>
              </div>

              <Button data-testid="payment-submit-btn" onClick={submit} disabled={submitting || !file || !utr.trim()} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full h-11 font-semibold">
                {submitting ? "Submitting..." : "Submit Payment for Verification"}
              </Button>
              <p className="text-xs text-center text-slate-500">
                Status will be <span className="font-semibold">Pending Verification</span> until an admin reviews it.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
